import type { LogFn } from '../../../log.js';
import { execGitFile } from '../../git-exec.js';
import { forEachWithGitYield } from '../../git-runtime.js';
import { parseNameStatusEntries } from '../../changes/parse/parse-name-status-entries.js';
import type { SnapshotRepoState } from '../types.js';
import { buildTrackedChangeMessage, buildUntrackedChangeMessage } from './messages.js';
import { newUntrackedPaths, repoDisplayPath, safeRelPath } from './paths.js';
import { pairDeletedFilesWithUntrackedMoves, readUntrackedTexts } from './move-pairs.js';

export async function collectRepoTurnChanges(options: {
  repo: SnapshotRepoState;
  multiRepo: boolean;
  sessionId: string;
  runId: string;
  sendSessionUpdate: (p: unknown) => void;
  log: LogFn;
}): Promise<void> {
  const { repo, multiRepo, sessionId, runId, sendSessionUpdate, log } = options;
  const displayPath = repoDisplayPath(repo.path, multiRepo);
  const seen = new Set<string>();
  const baselineRev = repo.stashSha || 'HEAD';

  let entries: ReturnType<typeof parseNameStatusEntries> = [];
  if (baselineRev) {
    let namesRaw: string;
    try {
      const { stdout } = await execGitFile(['diff', '--name-status', '-M', baselineRev], { cwd: repo.path });
      namesRaw = stdout;
    } catch (e) {
      log(
        `[turn-diff] Git diff --name-status failed in ${repo.path}: ${e instanceof Error ? e.message : String(e)}`
      );
      namesRaw = '';
    }
    entries = parseNameStatusEntries(namesRaw.split('\n'));
  }

  let untrackedRaw = '';
  try {
    const { stdout } = await execGitFile(['ls-files', '--others', '--exclude-standard'], { cwd: repo.path });
    untrackedRaw = stdout;
  } catch (e) {
    log(
      `[turn-diff] Git ls-files --others failed in ${repo.path}: ${e instanceof Error ? e.message : String(e)}`
    );
    return;
  }

  const untracked = newUntrackedPaths(untrackedRaw, repo.untrackedPaths ?? [], seen);
  const untrackedTextByRel = await readUntrackedTexts(repo.path, untracked);
  const { untrackedMoveByRel, movedDeletedRels } = await pairDeletedFilesWithUntrackedMoves({
    repoPath: repo.path,
    baselineRev,
    entries,
    untrackedTextByRel,
  });

  await forEachWithGitYield(entries, async (entry) => {
    const rel = entry.pathInRepo;
    const oldRel = entry.oldPathInRepo;
    if (movedDeletedRels.has(rel)) return;
    if (!safeRelPath(rel) || (oldRel !== undefined && !safeRelPath(oldRel))) return;
    seen.add(rel);
    try {
      const message = await buildTrackedChangeMessage({
        repoPath: repo.path,
        baselineRev,
        entry,
        displayPath,
        sessionId,
        runId,
      });
      if (message) sendSessionUpdate(message);
    } catch (e) {
      log(
        `[turn-diff] Git diff failed for ${rel}: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
  });

  await forEachWithGitYield(untracked, async (rel) => {
    if (!safeRelPath(rel)) return;
    try {
      const moved = untrackedMoveByRel.get(rel);
      const newText = moved?.newText ?? untrackedTextByRel.get(rel);
      if (newText === undefined) return;
      sendSessionUpdate(buildUntrackedChangeMessage({
        rel,
        displayPath,
        sessionId,
        runId,
        newText,
        moved,
      }));
    } catch (e) {
      log(
        `[turn-diff] Git diff failed for ${rel}: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
  });
}
